import { Head } from 'vite-react-ssg';
import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import LastUpdated from "@/components/LastUpdated";
import NavigationButtons from '@/components/NavigationButtons';
import RelatedLinks from '@/components/RelatedLinks';
import deepseekOcrHero from '@/assets/deepseek-ocr-hero.jpg';

const faqs = [
  {
    q: "What is DeepSeek OCR?",
    a: "DeepSeek-OCR is an open-source vision-language model that reads documents by turning pages into a small number of vision tokens instead of a long text sequence. Its purpose is context compression: the same page content costs roughly 7-20× fewer tokens, while OCR precision stays around 97% at 10× compression.",
  },
  {
    q: "Is DeepSeek OCR free?",
    a: "Yes. Code and model weights are published under DeepSeek's open-source release at github.com/deepseek-ai/DeepSeek-OCR, so you can run it locally or on your own GPU at no licence cost. You only pay for the hardware you run it on. It is not billed as a hosted API endpoint the way the V4 models are.",
  },
  {
    q: "How do I use DeepSeek OCR?",
    a: "Clone the repository, install the Python requirements, download the weights from Hugging Face, then call the model with an image or PDF page and a prompt describing the output you want (plain text, markdown, or structured layout). A single A100-class GPU is enough for high-volume batch parsing; smaller GPUs work for lower throughput.",
  },
  {
    q: "How accurate is DeepSeek OCR compared with other OCR models?",
    a: "In DeepSeek's published evaluation it stays near 97% decoding precision at a 10× compression ratio and outperforms GOT-OCR2.0 and MinerU2.0 while spending fewer vision tokens per page. Accuracy degrades gradually as you push the compression ratio past 10×, so pick a ratio per document type rather than globally.",
  },
  {
    q: "What hardware do I need to run DeepSeek OCR?",
    a: "A single modern data-centre GPU (A100 class) handles roughly 200k+ pages per day in DeepSeek's own reporting. For experimentation a consumer card with 16-24GB VRAM is workable at lower batch sizes, since the decoder is a 3B MoE with about 570M active parameters.",
  },
  {
    q: "Is DeepSeek OCR the same as DeepSeek's vision API?",
    a: "No. DeepSeek-OCR is a standalone open-source research model for document reading and context compression. Image understanding through the paid API runs on the experimental vision model in the V4 line, which is billed per token with a per-image token cap.",
  },
  {
    q: "What can you build with DeepSeek OCR?",
    a: "Document ingestion for RAG pipelines, invoice and contract parsing, archive digitisation, and long-context agents that would otherwise blow past their token budget. The compression angle matters most when you feed thousands of pages into a model per day.",
  },
];

const DeepseekOCR = () => {
  return (
    <>
      <Head>
        <title>DeepSeek OCR: How It Works, Free Setup &amp; Benchmarks</title>
        <meta name="description" content="DeepSeek OCR explained: 97% precision at 10× context compression, how to run it free from GitHub, hardware needs, benchmarks vs GOT-OCR2.0 and MinerU2.0, and where it fits next to V4 vision." />
        <meta name="keywords" content="DeepSeek OCR, optical character recognition, context compression, DeepEncoder, vision tokens, document parsing, AI OCR, DeepSeek AI, text compression, vision-language models, VLM, document AI" />
        <link rel="canonical" href="https://deepseek.ai/blog/deepseek-ocr-context-compression" />
        
        {/* Open Graph Meta Tags */}
        <meta property="og:title" content="DeepSeek-OCR: Revolutionary Context Compression Through Optical 2D Mapping" />
        <meta property="og:description" content="DeepSeek-OCR achieves 97% OCR precision at 10× compression ratio, surpassing GOT-OCR2.0 and MinerU2.0 while using fewer vision tokens. Learn about this breakthrough in AI document processing." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://deepseek.ai/blog/deepseek-ocr-context-compression" />
        <meta property="og:site_name" content="DeepSeek AI" />
        <meta property="og:image" content="https://deepseek.ai/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:image" content="https://deepseek.ai/og-image.png" />
        <meta property="article:published_time" content="2025-10-21T00:00:00Z" />
        <meta property="article:author" content="DeepSeek AI Research Team" />
        <meta property="article:section" content="AI Technology" />
        <meta property="article:tag" content="DeepSeek OCR" />
        <meta property="article:tag" content="Context Compression" />
        <meta property="article:tag" content="Vision-Language Models" />
        
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="DeepSeek-OCR: Revolutionary Context Compression Through Optical 2D Mapping" />
        <meta name="twitter:description" content="DeepSeek-OCR achieves 97% OCR precision at 10× compression, processing 200k+ pages per day with a single A100 GPU." />
        <meta name="twitter:creator" content="@DeepSeekAI" />
        
        {/* Additional SEO Meta Tags */}
        <meta name="author" content="DeepSeek AI Research Team" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />
        
        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "TechArticle",
            "headline": "DeepSeek-OCR: Revolutionary Context Compression Through Optical 2D Mapping",
            "description": "Discover DeepSeek-OCR's breakthrough in compressing long contexts via optical 2D mapping, achieving 97% OCR precision at 10× compression with DeepEncoder.",
            "author": {
              "@type": "Organization",
              "name": "The Independent DeepSeek Guide"
            },
            "publisher": {
              "@type": "Organization",
              "name": "The Independent DeepSeek Guide",
              "logo": {
                "@type": "ImageObject",
                "url": "https://deepseek.ai/logo.png"
              }
            },
            "datePublished": "2025-10-21T00:00:00Z",
            "dateModified": "2026-08-25T00:00:00Z",
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": "https://deepseek.ai/blog/deepseek-ocr-context-compression"
            },
            "articleSection": "AI Technology",
            "keywords": ["DeepSeek OCR", "Context Compression", "DeepEncoder", "Vision Tokens", "Document Parsing", "AI OCR"],
            "about": [
              {
                "@type": "Thing",
                "name": "Optical Character Recognition"
              },
              {
                "@type": "Thing",
                "name": "Context Compression"
              },
              {
                "@type": "Thing",
                "name": "Vision-Language Models"
              }
            ]
          })}
        </script>
        
        {/* FAQ Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map((f) => ({
              "@type": "Question",
              "name": f.q,
              "acceptedAnswer": { "@type": "Answer", "text": f.a },
            })),
          })}
        </script>

        {/* BreadcrumbList Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://deepseek.ai/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Blog",
                "item": "https://deepseek.ai/blog"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": "DeepSeek-OCR: Context Compression",
                "item": "https://deepseek.ai/blog/deepseek-ocr-context-compression"
              }
            ]
          })}
        </script>
      </Head>

      <article className="min-h-screen pt-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Hero Image */}
          <div className="relative w-full h-64 md:h-96 mb-8 rounded-xl overflow-hidden shadow-lg">
            <img 
              src={deepseekOcrHero} 
              alt="DeepSeek-OCR: AI-powered document understanding with context compression technology"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-4 left-4 right-4">
              <span className="inline-block px-3 py-1 bg-blue-600 text-white text-sm rounded-full mb-2">AI Technology</span>
              <p className="text-white/80 text-sm">12 min read</p>
            </div>
          </div>

          <LastUpdated date="2026-08-25" className="mb-4" />

          <header className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              DeepSeek-OCR: Revolutionary Context Compression Through Optical 2D Mapping
            </h1>
            <div className="flex items-center gap-4 text-sm text-gray-600 flex-wrap">
              <time dateTime="2025-10-21">October 21, 2025</time>
              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full">AI Technology</span>
              <span>By DeepSeek AI Research Team</span>
            </div>
          </header>

          <Card className="p-8 mb-8">
            <section className="prose prose-lg max-w-none">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Abstract: A New Paradigm for Context Compression</h2>
              <p className="text-gray-700 mb-6">
                DeepSeek AI has unveiled <strong>DeepSeek-OCR</strong>, a groundbreaking approach to compressing long contexts via optical 2D mapping. This innovative system demonstrates that vision-based compression can achieve remarkable efficiency in handling text-heavy documents, potentially revolutionizing how large language models (LLMs) process extensive textual information.
              </p>
              <p className="text-gray-700 mb-6">
                The DeepSeek-OCR system consists of two primary components: <strong>DeepEncoder</strong> and <strong>DeepSeek3B-MoE-A570M</strong> as the decoder. Together, they achieve an impressive <strong>97% OCR precision</strong> when compressing text at a ratio of less than 10× (meaning 10 text tokens compressed into 1 vision token). Even at an aggressive 20× compression ratio, the system maintains approximately 60% accuracy.
              </p>
            </section>
          </Card>

          <Card className="p-8 mb-8">
            <section className="prose prose-lg max-w-none">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">What Makes DeepSeek-OCR Revolutionary?</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">1. Exceptional Compression Ratios with High Accuracy</h3>
              <p className="text-gray-700 mb-4">
                The core innovation of DeepSeek-OCR lies in its ability to compress textual information dramatically while maintaining high accuracy:
              </p>
              <ul className="list-disc pl-6 mb-6 text-gray-700">
                <li><strong>96%+ OCR precision</strong> at 9-10× compression ratio</li>
                <li><strong>~90% accuracy</strong> at 10-12× compression ratio</li>
                <li><strong>~60% accuracy</strong> at 20× compression ratio</li>
              </ul>
              <p className="text-gray-700 mb-6">
                These results demonstrate that compact language models can effectively decode compressed visual representations, suggesting that larger LLMs could readily acquire similar capabilities through appropriate pretraining design.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">2. DeepEncoder: 250M Parameters, Maximum Efficiency</h3>
              <p className="text-gray-700 mb-4">
                <strong>DeepEncoder</strong> is a lightweight yet powerful vision encoder with only <strong>250 million parameters</strong>. It achieves an impressive <strong>32× compression ratio</strong> while maintaining lossless performance. Key features include:
              </p>
              <ul className="list-disc pl-6 mb-6 text-gray-700">
                <li>Serial connection of window attention and global attention encoder components</li>
                <li>16× convolutional compressor that reduces vision tokens before entering dense global attention</li>
                <li><strong>Multi-Head Latent Attention (MLA)</strong> mechanism for efficient cross-modal alignment</li>
                <li>Ability to handle large images without GPU memory overflow</li>
                <li>Effective memory and token compression for optimal performance</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">3. State-of-the-Art Performance with Minimal Tokens</h3>
              <p className="text-gray-700 mb-4">
                On the <strong>OmniDocBench</strong> benchmark, DeepSeek-OCR achieves remarkable efficiency:
              </p>
              <ul className="list-disc pl-6 mb-6 text-gray-700">
                <li>Surpasses <strong>GOT-OCR2.0</strong> (which uses 256 tokens/page) using only <strong>100 vision tokens</strong></li>
                <li>Outperforms <strong>MinerU2.0</strong> (which averages 6000+ tokens per page) while utilizing fewer than <strong>800 vision tokens</strong></li>
                <li>Achieves state-of-the-art performance among end-to-end models while using the fewest vision tokens</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">4. Massive Production Scalability</h3>
              <p className="text-gray-700 mb-6">
                DeepSeek-OCR demonstrates exceptional real-world performance, capable of generating training data for LLMs and VLMs at an unprecedented scale:
              </p>
              <ul className="list-disc pl-6 mb-6 text-gray-700">
                <li><strong>200,000+ pages per day</strong> with a single A100-40G GPU</li>
                <li><strong>33 million pages per day</strong> using 20 nodes (160 A100-40G GPUs)</li>
                <li>Practical deployment for large-scale document processing tasks</li>
              </ul>
            </section>
          </Card>

          <Card className="p-8 mb-8">
            <section className="prose prose-lg max-w-none">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">The Technical Architecture Behind DeepSeek-OCR</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">Vision Encoder Comparison</h3>
              <p className="text-gray-700 mb-4">
                Current open-source vision-language models (VLMs) employ three main types of vision encoders, each with distinct advantages and limitations:
              </p>
              <ul className="list-disc pl-6 mb-6 text-gray-700">
                <li><strong>Dual-tower architecture</strong> (e.g., Vary): Offers controllable parameters but requires complex dual image preprocessing</li>
                <li><strong>Tile-based methods</strong> (e.g., InternVL2.0): Reduces activation memory but can result in excessive fragmentation and numerous vision tokens</li>
                <li><strong>Adaptive resolution encoding</strong> (e.g., Qwen2-VL): Handles diverse resolutions flexibly but faces challenges with massive activation memory consumption</li>
              </ul>
              <p className="text-gray-700 mb-6">
                DeepEncoder addresses these limitations by combining the best aspects of each approach while minimizing their drawbacks, achieving a balance between memory efficiency, token count, and processing capability.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">Multi-Resolution Support</h3>
              <p className="text-gray-700 mb-6">
                DeepEncoder is designed to support multiple resolutions efficiently, enabling it to process documents of varying sizes and complexities without sacrificing performance or requiring excessive computational resources.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">The MoE Decoder Architecture</h3>
              <p className="text-gray-700 mb-6">
                The decoder component utilizes <strong>DeepSeek3B-MoE-A570M</strong>, a mixture-of-experts architecture that provides efficient inference while maintaining high accuracy. This design enables the model to specialize in different aspects of OCR tasks while sharing knowledge across experts.
              </p>
            </section>
          </Card>

          <Card className="p-8 mb-8">
            <section className="prose prose-lg max-w-none">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Practical Applications and Use Cases</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">Historical Document Compression</h3>
              <p className="text-gray-700 mb-6">
                DeepSeek-OCR shows considerable promise for research areas such as historical long-context compression, enabling efficient digitization and processing of archival materials without requiring massive storage or computational resources.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">Memory Mechanisms in LLMs</h3>
              <p className="text-gray-700 mb-6">
                The vision-text compression paradigm opens new possibilities for implementing memory forgetting mechanisms in LLMs, allowing models to efficiently store and retrieve historical context while managing computational constraints.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">Enhanced Document Parsing</h3>
              <p className="text-gray-700 mb-4">
                Beyond standard OCR, DeepSeek-OCR includes capabilities for parsing:
              </p>
              <ul className="list-disc pl-6 mb-6 text-gray-700">
                <li>Charts and graphs with high accuracy</li>
                <li>Chemical formulas and scientific notation</li>
                <li>Simple geometric figures and diagrams</li>
                <li>Natural images with embedded text</li>
                <li>Multilingual documents across various languages</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">Training Data Generation</h3>
              <p className="text-gray-700 mb-6">
                The model's ability to process 200,000+ pages daily on a single GPU makes it an ideal tool for generating high-quality training data for next-generation LLMs and VLMs at scale.
              </p>
            </section>
          </Card>

          <Card className="p-8 mb-8">
            <section className="prose prose-lg max-w-none">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">A New Paradigm: "A Picture is Worth a Thousand Words"</h2>
              <p className="text-gray-700 mb-6">
                DeepSeek-OCR addresses a crucial research question that current models haven't adequately explored: <em>"For a document containing 1000 words, how many vision tokens are at least needed for decoding?"</em>
              </p>
              <p className="text-gray-700 mb-6">
                The answer has profound implications for the fundamental principle that "a picture is worth a thousand words." DeepSeek-OCR demonstrates that a single image containing document text can represent rich information using substantially fewer tokens than the equivalent digital text, suggesting that optical compression through vision tokens can achieve much higher compression ratios than traditional text encoding.
              </p>
              <p className="text-gray-700 mb-6">
                This paradigm shift reexamines vision-language models (VLMs) from an LLM-centric perspective, focusing on how vision encoders can enhance LLMs' efficiency in processing textual information rather than solely focusing on visual question answering (VQA) tasks.
              </p>
            </section>
          </Card>

          <Card className="p-8 mb-8">
            <section className="prose prose-lg max-w-none">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Engine and Training Pipeline</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">Comprehensive Data Collection</h3>
              <p className="text-gray-700 mb-4">
                The DeepSeek-OCR data engine incorporates multiple data sources:
              </p>
              <ul className="list-disc pl-6 mb-6 text-gray-700">
                <li><strong>OCR 1.0 data</strong>: Traditional OCR datasets for baseline training</li>
                <li><strong>OCR 2.0 data</strong>: Advanced synthetic and real-world document data</li>
                <li><strong>General vision data</strong>: Diverse image datasets for broader visual understanding</li>
                <li><strong>Text-only data</strong>: Pure language data to enhance decoder capabilities</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">Three-Stage Training Process</h3>
              <p className="text-gray-700 mb-4">
                The training pipeline follows a systematic three-stage approach:
              </p>
              <ol className="list-decimal pl-6 mb-6 text-gray-700">
                <li><strong>Pre-training DeepEncoder</strong>: First stage focuses on training the vision encoder to learn efficient visual representations and compression capabilities</li>
                <li><strong>Pre-training Decoder</strong>: Second stage pre-trains the DeepSeek3B-MoE-A570M decoder to understand compressed visual tokens</li>
                <li><strong>Supervised Fine-Tuning (SFT)</strong>: Final stage fine-tunes the complete end-to-end system on high-quality OCR data for optimal document understanding</li>
              </ol>
            </section>
          </Card>

          <Card className="p-8 mb-8">
            <section className="prose prose-lg max-w-none">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Open Source Availability</h2>
              <p className="text-gray-700 mb-6">
                True to DeepSeek AI's commitment to open research, both the code and model weights for DeepSeek-OCR are publicly accessible at <a href="https://github.com/deepseek-ai/DeepSeek-OCR" className="text-blue-600 hover:text-blue-800 underline" target="_blank" rel="noopener noreferrer">github.com/deepseek-ai/DeepSeek-OCR</a>.
              </p>
              <p className="text-gray-700 mb-6">
                This open-source release enables researchers and developers worldwide to:
              </p>
              <ul className="list-disc pl-6 mb-6 text-gray-700">
                <li>Reproduce and validate the research findings</li>
                <li>Build upon the DeepSeek-OCR architecture for custom applications</li>
                <li>Contribute improvements and extensions to the community</li>
                <li>Deploy the system for production use cases</li>
              </ul>
            </section>
          </Card>

          <Card className="p-8 mb-8">
            <section className="prose prose-lg max-w-none">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">DeepSeek OCR — frequently asked questions</h2>
              <Accordion type="single" collapsible className="not-prose">
                {faqs.map((f, i) => (
                  <AccordionItem key={f.q} value={`ocr-faq-${i}`}>
                    <AccordionTrigger className="text-left font-semibold text-gray-900">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-gray-700">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>
          </Card>

          <Card className="p-8 mb-8 border-blue-200 bg-blue-50/70">
            <section className="prose prose-lg max-w-none">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Where OCR fits in the current DeepSeek stack</h2>
              <p className="text-gray-700 mb-4">
                DeepSeek-OCR is a research model you self-host. If you want image understanding through
                the paid API instead, that lives in the V4 line and is billed per token:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-0">
                <li><Link to="/blog/deepseek-vision-api-guide" className="text-blue-600 underline">The DeepSeek vision API guide</Link> — image inputs, the per-image token cap and the experimental limits</li>
                <li><Link to="/deepseek-api" className="text-blue-600 underline">DeepSeek API setup</Link> — keys, OpenAI-compatible calls and current model strings</li>
                <li><Link to="/pricing" className="text-blue-600 underline">Current DeepSeek pricing</Link> — live rate card and a cost calculator</li>
              </ul>
            </section>
          </Card>

          <Card className="p-8 mb-8 bg-blue-50">
            <section className="prose prose-lg max-w-none">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Conclusion: Toward More Efficient LLMs</h2>
              <p className="text-gray-700 mb-6">
                DeepSeek-OCR represents a significant step forward in addressing one of the most pressing challenges in modern AI: efficiently processing long textual contexts. By leveraging visual modality as a compression medium, the system demonstrates that substantial token reduction (7-20×) is achievable for different context stages while maintaining high accuracy.
              </p>
              <p className="text-gray-700 mb-6">
                The quantitative analysis provided by DeepSeek-OCR offers empirical guidelines for optimizing VLM token allocation, while the DeepEncoder architecture showcases practical feasibility with real-world deployment capabilities. Although focused on OCR as a proof-of-concept, this paradigm opens new possibilities for rethinking how vision and language modalities can be synergistically combined to enhance computational efficiency in large-scale text processing and agent systems.
              </p>
              <p className="text-gray-700 mb-6">
                As LLMs continue to grow in size and capability, innovations like DeepSeek-OCR will be crucial for making these powerful models more accessible, efficient, and practical for real-world applications. The future of AI may well depend on such creative approaches to the fundamental challenges of context management and computational efficiency.
              </p>
            </section>
          </Card>

          <NavigationButtons />

          <div className="mt-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Related DeepSeek AI Resources</h2>
            <RelatedLinks />
          </div>
        </div>
      </article>
    </>
  );
};

export default DeepseekOCR;
